<template>
  <div class="row g-4">
    <div class="col-lg-7 col-md-6">
      <DxForm :col-count="3" id="formularioAnexoEmpresaServicios" ref="formInstance" :form-data="modelo" label-location="top"
        :show-colon-after-label="false" validation-group="validacionAnexoEmpresaServicios">
        <DxSimpleItem :col-span="1" data-field="Nombre" editor-type="dxTextBox">
          <DxLabel :text="$t('anexos_empresa_servicios.form.nombre')" />
          <DxRequiredRule :message="$t('anexos_empresa_servicios.form.validacion.nombre')" />
          <DxStringLengthRule :max="255" :message="$t('anexos_empresa_servicios.form.validacion.nombre_longitud')" />
        </DxSimpleItem>
        <DxSimpleItem :col-span="2" data-field="Descripcion" editor-type="dxTextBox">
          <DxLabel :text="$t('anexos_empresa_servicios.form.descripcion')" />
        </DxSimpleItem>
        <DxSimpleItem :col-span="3" data-field="ContenidoHtml" editor-type="dxTextArea"
          :editor-options="{ height: 600, readOnly: asociado }">
          <DxLabel :text="$t('anexos_empresa_servicios.form.contenido_html')" />
          <DxRequiredRule :message="$t('anexos_empresa_servicios.form.validacion.contenido_html')" />
        </DxSimpleItem>
      </DxForm>
    </div>
    <div class="col-lg-5 col-md-6">
      <div class="d-flex justify-content-between align-items-center mb-2">
        <span class="dx-field-item-label-text">{{ $t("anexos_empresa_servicios.form.variables.titulo") }}</span>
        <small v-if="asociado" class="text-muted fst-italic">
          {{ $t("anexos_empresa_servicios.form.variables.nota_variables_bloqueadas") }}
        </small>
      </div>
      <DxDataGrid ref="variablesGrid" :height="700" :data-source="variablesStore" @init-new-row="onInitNewRow">
        <DxEditing :allow-adding="!asociado" :allow-updating="!asociado" :allow-deleting="!asociado" mode="row" />
        <DxScrolling mode="virtual"/>
        <DxColumn data-field="Codigo" :caption="$t('anexos_empresa_servicios.form.variables.codigo')" />
        <DxColumn data-field="Tipo" :caption="$t('anexos_empresa_servicios.form.variables.tipo')"
          :lookup="tipoLookup" />
      </DxDataGrid>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed, reactive } from "vue";
import { ref } from "vue";
import {
  DxForm,
  DxLabel,
  DxRequiredRule,
  DxSimpleItem,
  DxStringLengthRule,
} from "devextreme-vue/form";
import {
  DxDataGrid,
  DxColumn,
  DxEditing,
  DxScrolling
} from "devextreme-vue/data-grid";
import ArrayStore from "devextreme/data/array_store";
import { useIntl } from "@services/i18n";
import validationEngine from "devextreme/ui/validation_engine";
import { useApi } from "@api/integration";
import { toastError, toastSuccess } from "@/.shared/services/notification-service";
import {
  type AnexoEmpresaServiciosDetailDto,
  type CreateAnexoEmpresaServiciosDto,
  type EditAnexoEmpresaServiciosDto,
  type DefinicionVariableAnexoDto,
} from "@api/api";

type GuardarAnexoResult = { success: boolean; newId?: string };

const api = useApi();
const formInstance = ref<DxForm>();
const variablesGrid = ref<DxDataGrid>();
const intl = useIntl();

const props = defineProps<{
  detail: AnexoEmpresaServiciosDetailDto | null;
  idEmpresaServicios: string;
}>();

const isEditMode = computed(() => !!props.detail);
const asociado = computed(() => props.detail?.AsociadoEnOfertasOServicios ?? false);

const modelo = reactive({
  Nombre: props.detail?.Nombre ?? "",
  Descripcion: props.detail?.Descripcion ?? null as string | null,
  ContenidoHtml: props.detail?.ContenidoHtml ?? "",
});

const variableRows: DefinicionVariableAnexoDto[] = [...(props.detail?.DefinicionVariables ?? [])];

const variablesStore = new ArrayStore({
  data: variableRows,
  key: "Codigo",
});

const tipoLookup = {
  dataSource: [
    { value: "BOOL", text: "Checkbox" },
    { value: "STRING", text: "Texto" },
  ],
  valueExpr: "value",
  displayExpr: "text",
};

function onInitNewRow(e: any) {
  e.data.Tipo = "BOOL";
}

async function guardar(): Promise<GuardarAnexoResult> {
  const validacion = validationEngine.validateGroup("validacionAnexoEmpresaServicios");
  if (!validacion.isValid) {
    toastError(intl.i18n.t("anexos_empresa_servicios.form.revise_validacion"));
    return { success: false };
  }

  if (!asociado.value) {
    await variablesGrid.value?.instance?.saveEditData();

    const codigos = variableRows.map(v => v.Codigo.trim().toUpperCase());
    if (new Set(codigos).size !== codigos.length) {
      toastError(intl.i18n.t("anexos_empresa_servicios.form.variables.error_codigos_duplicados"));
      return { success: false };
    }
  }

  const variables: DefinicionVariableAnexoDto[] = variableRows.map(
    ({ Codigo, Tipo }) => ({ Codigo: Codigo.trim(), Tipo })
  );

  try {
    if (isEditMode.value) {
      const dto: EditAnexoEmpresaServiciosDto = {
        Id: props.detail!.Id,
        Nombre: modelo.Nombre,
        Descripcion: modelo.Descripcion,
        ContenidoHtml: modelo.ContenidoHtml,
        DefinicionVariables: variables,
      };
      await api.callStandardApi("editAnexoEmpresaServicios", { showLoading: true }, dto);
      toastSuccess(intl.i18n.t("anexos_empresa_servicios.edicion_exito"));
      return { success: true };
    }

    const dto: CreateAnexoEmpresaServiciosDto = {
      IdEmpresaServicios: props.idEmpresaServicios,
      Nombre: modelo.Nombre,
      Descripcion: modelo.Descripcion,
      ContenidoHtml: modelo.ContenidoHtml,
      DefinicionVariables: variables,
    };

    const id = await api.callStandardApi("createAnexoEmpresaServicios", { showLoading: true }, dto);
    toastSuccess(intl.i18n.t("anexos_empresa_servicios.creacion_exito"));
    return { success: true, newId: id as string };
  } catch {
    toastError(intl.i18n.t(isEditMode.value ? "anexos_empresa_servicios.edicion_fallo" : "anexos_empresa_servicios.creacion_fallo"));
    return { success: false };
  }
}

defineExpose({ guardar });
</script>

<style lang="scss" scoped>
</style>
